import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Loader2, RefreshCw, Search, X, ChevronLeft } from 'lucide-react';
import { formatProductLabel } from '../utils/formatDisplayLabel';

export const ProductInputPanel = ({
  onAnalyze,
  isLoading,
  error,
  onReset,
  onCancel,
  resetKey,
  brands,
  variants,
  onSelectBrand,
  onSelectVariant,
  onCustomVariant,
  onRefineProductSearch,
  onBackSelection,
  onClearCache,
  serverCacheFeedback,
  pendingConfirmation,
  onConfirmProduct,
  inputRef,
  errorCode,
}) => {
  const [productInput, setProductInput] = useState('');
  const [customVariant, setCustomVariant] = useState('');
  const [selectedBrandName, setSelectedBrandName] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const pickerRef = useRef(null);

  useEffect(() => {
    setProductInput('');
    setCustomVariant('');
    setSelectedBrandName(null);
    setIsExpanded(false);
  }, [resetKey]);

  const hasBrands = Array.isArray(brands) && brands.length >= 2;
  const hasVariants = Array.isArray(variants) && variants.length > 0;
  const friendlyError = useMemo(() => {
    const text = String(error || '').trim();
    if (!text) return null;
    const code = String(errorCode || '').trim().toUpperCase();

    if (code === 'NO_INGREDIENTS_FOUND') {
      return {
        kind: 'searchMiss',
        title: "That search didn't bear fruit",
        body: "We looked through the aisles — nothing matched that product name. Try brand + full flavor, or scan the barcode, snap the label, or enter the ingredients below, and we'll check it out.",
        actionLabel: 'Search again',
        tips: [],
      };
    }

    if (code === 'EXACT_PRODUCT_SOURCE_NOT_FOUND') {
      return {
        kind: 'sourceGap',
        title: 'This one needs a closer look',
        body: 'The ingredient list is playing hide-and-seek.\nScan the barcode, snap the label, or enter the ingredients below, and we’ll check it out.',
        actionLabel: 'Search again',
        tips: [],
      };
    }

    if (['CONNECTION_ERROR', 'TIMEOUT', 'NETWORK'].includes(code)) {
      return {
        kind: 'connection',
        title: 'We lost the signal',
        body: "VegAI couldn't reach the server. Check your connection and try again!",
        actionLabel: 'Try again',
        tips: [],
      };
    }

    if (code === 'SERVER_ERROR') {
      return {
        kind: 'server',
        title: "Sorry — we're having a moment",
        body: 'Our server hit a snag. Give it a moment and try again.',
        actionLabel: 'Try again',
        tips: [],
      };
    }

    if (code === 'RATE_LIMITED') {
      return {
        kind: 'rateLimit',
        title: "One sec, our cart's full",
        body: 'Easy there — kindly wait a moment, then try again!',
        actionLabel: 'Try again',
        tips: [],
      };
    }

    return {
      kind: 'error',
      title: 'Product lookup needs another try',
      body: text,
      actionLabel: 'Check another product',
      tips: [
        { icon: Search, text: 'Try brand + full flavor name.' },
        { icon: RefreshCw, text: 'Clear cache, then search again.' },
      ],
    };
  }, [error, errorCode]);

  useEffect(() => {
    if ((hasBrands || hasVariants) && pickerRef.current) {
      pickerRef.current.focus();
    }
  }, [hasBrands, hasVariants]);

  const handleProductCheck = () => {
    if (!isExpanded) {
      setIsExpanded(true);
      window.requestAnimationFrame(() => inputRef?.current?.focus?.());
      return;
    }
    if (!productInput.trim()) return;
    setSelectedBrandName(null);
    onAnalyze(productInput);
  };

  const handleCustomVariantSubmit = () => {
    if (!customVariant.trim()) return;
    const text = customVariant.trim();
    if (hasBrands && typeof onRefineProductSearch === 'function') {
      onRefineProductSearch(text);
    } else if (typeof onCustomVariant === 'function') {
      onCustomVariant(text);
    }
    setCustomVariant('');
  };

  const handleBrandSelect = (brandObj) => {
    setSelectedBrandName(brandObj.brand);
    if (typeof onSelectBrand === 'function') onSelectBrand(brandObj);
  };

  const handleReset = () => {
    setProductInput('');
    setCustomVariant('');
    setSelectedBrandName(null);
    if (typeof onReset === 'function') onReset();
  };

  const handleClose = () => {
    if (isLoading) return;
    setProductInput('');
    setCustomVariant('');
    setSelectedBrandName(null);
    setIsExpanded(false);
    if (typeof onReset === 'function') onReset();
  };

  const handleBackSelection = () => {
    if (isLoading) return;
    setCustomVariant('');
    setSelectedBrandName(null);
    if (typeof onBackSelection === 'function') onBackSelection();
  };

  const optionKey = (v) => {
    if (typeof v === 'object' && v !== null) {
      if (v.offCode != null) return `off:${v.offCode}`;
      if (v.fdcId != null) return `fdc:${v.fdcId}`;
      if (v.label != null) return `lbl:${v.label}`;
      return JSON.stringify(v);
    }
    return String(v);
  };

  const optionLabel = (v) => {
    const raw =
      typeof v === 'object' && v !== null && v.label != null ? String(v.label) : String(v);
    return formatProductLabel(raw);
  };

  const variantDedupeKey = (label) => String(label || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .filter((token) => !['cheese', 'flavor', 'flavored'].includes(token))
    .join(' ');

  const visibleVariants = useMemo(() => {
    if (!hasVariants) return [];
    const bestByKey = new Map();
    for (const variant of variants) {
      const label = optionLabel(variant);
      const key = variantDedupeKey(label);
      if (!key) continue;
      const current = bestByKey.get(key);
      if (!current || label.length > current.label.length) {
        bestByKey.set(key, { variant, label });
      }
    }
    return Array.from(bestByKey.values());
  }, [hasVariants, variants]);

  return (
    <div>
      {!isExpanded && !isLoading && !hasBrands && !hasVariants && !pendingConfirmation && !error ? (
        <button
          type="button"
          onClick={handleProductCheck}
          className="mockup-row-button mockup-row-button--green mb-4"
        >
          <span className="mockup-row-icon mockup-row-icon--green">
            <Search className="h-5 w-5" />
          </span>
          <span className="mockup-row-copy">
            <span className="mockup-row-title text-emerald-700">Product search</span>
            <span className="mockup-row-subtitle">Look up by name</span>
          </span>
          <span className="mockup-row-arrow text-emerald-600">→</span>
        </button>
      ) : (
      <>
      <div className="mb-2 flex items-center justify-between gap-3">
        <label htmlFor="productName" className="vegai-section-label block">
          Product search
        </label>
        {!isLoading && (
          <button
            type="button"
            onClick={handleClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
            aria-label="Close search panel"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <input
          id="productName"
          ref={inputRef}
          type="text"
          value={productInput}
          onChange={(e) => setProductInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey) {
              e.preventDefault();
              if (productInput.trim() && !isLoading) handleProductCheck();
            }
          }}
          placeholder="Look up by name"
          maxLength={200}
          className="vegai-input min-w-0 sm:flex-1"
          disabled={isLoading}
        />
        <button
          onClick={handleProductCheck}
          disabled={!productInput.trim() || isLoading}
          className="vegai-primary-button w-full whitespace-nowrap sm:w-auto"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Analyzing...
            </>
          ) : (
            'Analyze Product'
          )}
        </button>
        {isLoading && (
          <button
            type="button"
            onClick={() => { if (typeof onCancel === 'function') onCancel(); }}
            className="vegai-secondary-button w-full whitespace-nowrap sm:w-auto"
          >
            Cancel
          </button>
        )}
      </div>
      </>
      )}

      {/* Cache clear is intentionally hidden from the idle layout to match the
          home screen design. It only appears once the user has expanded the
          search field, an error is showing, or a cache feedback toast needs to
          stay visible — preserving the underlying functionality. */}
      {typeof onClearCache === 'function' && (isExpanded || error || serverCacheFeedback?.message) && (
        <div className="mt-1 mb-1 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => onClearCache()}
            className="text-xs font-medium text-emerald-700 hover:text-emerald-900 underline"
          >
            Clear cache
          </button>
          {serverCacheFeedback?.message && (
            <span
              className={`text-xs ${serverCacheFeedback.variant === 'error' ? 'text-red-600' : 'text-emerald-700'}`}
              role="status"
            >
              {serverCacheFeedback.message}
            </span>
          )}
        </div>
      )}

      {/* Step 1: Brand picker */}
      {hasBrands && (
        <div ref={pickerRef} tabIndex={-1} role="region" aria-label="Select a brand" className="relative mt-3 rounded-3xl border border-sky-100 bg-sky-50/80 p-4 outline-none">
          <button type="button" onClick={handleReset} className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close and clear search">
            <X className="w-5 h-5" />
          </button>
          <p className="text-sm font-bold text-sky-900 mb-3 pr-8">Multiple brands found. Which brand?</p>
          <div className="flex flex-wrap gap-2 mb-3" role="group" aria-label="Brand options">
            {brands.map((brandObj) => (
              <button
                key={brandObj.brand}
                type="button"
                onClick={() => handleBrandSelect(brandObj)}
                disabled={isLoading}
                aria-label={`Select brand ${brandObj.brand}`}
                className="rounded-full border border-sky-200 bg-white px-3 py-1.5 text-sm font-semibold text-sky-800 transition-colors hover:bg-sky-100 hover:border-sky-300 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {formatProductLabel(brandObj.brand)}
              </button>
            ))}
          </div>
          <label htmlFor="customBrandInput" className="text-xs font-bold uppercase tracking-wide text-sky-800 mb-1.5 block">Other</label>
          <div className="flex gap-2">
            <input
              id="customBrandInput"
              type="text"
              value={customVariant}
              onChange={(e) => setCustomVariant(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCustomVariantSubmit(); } }}
              placeholder="Brand or product name"
              className="vegai-input flex-1 py-2"
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={handleCustomVariantSubmit}
              disabled={!customVariant.trim() || isLoading}
              className="vegai-primary-button px-3 py-2"
            >
              Go
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Product/flavor picker (after brand selected, or single-brand multi-product) */}
      {!hasBrands && hasVariants && (
        <div ref={hasBrands ? undefined : pickerRef} tabIndex={-1} role="region" aria-label="Select a product version" className="relative mt-3 rounded-3xl border border-sky-100 bg-sky-50/80 p-4 outline-none">
          <div className="flex items-center justify-between gap-2 mb-3">
            {typeof onBackSelection === 'function' ? (
              <button
                type="button"
                onClick={handleBackSelection}
                disabled={isLoading}
                className="inline-flex items-center gap-1 text-sm font-semibold text-sky-800 hover:text-sky-950 disabled:cursor-not-allowed disabled:opacity-50"
                aria-label="Go back to brand selection"
              >
                <ChevronLeft className="w-4 h-4 shrink-0" aria-hidden />
                Back
              </button>
            ) : (
              <span />
            )}
            <button type="button" onClick={handleReset} className="text-gray-400 hover:text-gray-600 transition-colors p-1 -mr-1" aria-label="Close and clear search">
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-sm font-bold text-sky-900 mb-3">
            {selectedBrandName
              ? `Which ${formatProductLabel(selectedBrandName)} product?`
              : 'Multiple versions found. Which one?'}
          </p>
          <div className="flex flex-wrap gap-2 mb-3" role="group" aria-label="Product version options">
            {visibleVariants.map(({ variant, label }, idx) => (
              <button
                key={`${optionKey(variant)}-${idx}`}
                type="button"
                onClick={() => { if (typeof onSelectVariant === 'function') onSelectVariant(variant); }}
                disabled={isLoading}
                aria-label={`Select ${label}`}
                className="rounded-full border border-sky-200 bg-white px-3 py-1.5 text-sm font-semibold text-sky-800 transition-colors hover:bg-sky-100 hover:border-sky-300 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <label htmlFor="customVariantInput" className="sr-only">Custom version</label>
            <input
              id="customVariantInput"
              type="text"
              value={customVariant}
              onChange={(e) => setCustomVariant(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCustomVariantSubmit(); } }}
              placeholder="Or type a specific version..."
              className="vegai-input flex-1 py-2"
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={handleCustomVariantSubmit}
              disabled={!customVariant.trim() || isLoading}
              className="vegai-primary-button px-3 py-2"
            >
              Go
            </button>
          </div>
        </div>
      )}

      {/* Product confirmation step — shown when a single match is found */}
      {!hasBrands && !hasVariants && pendingConfirmation && (
        <div className="relative mt-3 rounded-3xl border border-emerald-100 bg-emerald-50/80 p-4">
          <button type="button" onClick={handleReset} className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close and clear search">
            <X className="w-5 h-5" />
          </button>
          <p className="text-sm text-emerald-800 mb-1 pr-8">We found a match:</p>
          <p className="text-base font-bold text-emerald-950 mb-3">
            {formatProductLabel(pendingConfirmation.productMatch || pendingConfirmation.originalInput)}
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => { if (typeof onConfirmProduct === 'function') onConfirmProduct(); }}
              disabled={isLoading}
              className="vegai-primary-button px-4 py-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin inline" />
                  Analyzing...
                </>
              ) : (
                'Yes, analyze this'
              )}
            </button>
            <button
              type="button"
              onClick={handleReset}
              disabled={isLoading}
              className="vegai-secondary-button px-4 py-2"
            >
              Not what I meant
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="mt-3 rounded-2xl border border-red-200 bg-red-50 p-4">
          <div className="flex items-start text-red-800">
            <Search className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-black">{friendlyError?.title || 'Product lookup needs another try'}</p>
              <p className="mt-1 whitespace-pre-line text-sm leading-6 text-red-700">
                {friendlyError?.body || error}
              </p>
              {friendlyError?.tips?.length > 0 && (
                <ul
                  className={
                    friendlyError?.kind === 'sourceGap'
                      ? 'mt-3 space-y-2 text-sm leading-6 text-red-700'
                      : 'mt-3 grid gap-2'
                  }
                >
                  {friendlyError.tips.map(({ icon: Icon, text }) => (
                    friendlyError?.kind === 'sourceGap' ? (
                      <li key={text} className="flex items-start gap-2">
                        <Icon className="h-4 w-4 shrink-0 mt-0.5" />
                        <span>{text}</span>
                      </li>
                    ) : (
                      <div key={text} className="flex items-center gap-2 rounded-xl bg-white/70 px-3 py-2 text-xs font-semibold text-red-800">
                        <Icon className="h-4 w-4 shrink-0" />
                        <span>{text}</span>
                      </div>
                    )
                  ))}
                </ul>
              )}
              <div className="mt-3">
                <button
                  type="button"
                  className="text-sm font-semibold text-red-700 underline hover:text-red-800"
                  onClick={handleReset}
                >
                  {friendlyError?.actionLabel || 'Check another product'}
                </button>
                {friendlyError?.kind === 'error' && typeof onClearCache === 'function' && (
                  <button type="button" className="ml-3 text-sm font-semibold text-red-700 underline hover:text-red-800" onClick={() => onClearCache()}>
                    Clear cache
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
